// =========================================================
// DEBUTS CROP
// PREMIUM GROW SHOP
// =========================================================
//
// BLOG · BACKEND INTEGRATION
//
// DESARROLLO:
// VITE_API_URL=http://localhost:8000/api
//
// PRODUCCIÓN:
// VITE_API_URL=https://api.debutscrop.com/api
//
// =========================================================

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Link,
  useSearchParams,
} from "react-router-dom";

import "./blog.css";

import blogBg from "../../assets/bg.png";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";


// =========================================================
// API CONFIG
// =========================================================
//
// IMPORTANTE:
//
// NO colocar directamente:
//
// http://localhost:8000
//
// ni:
//
// https://api.debutscrop.com
//
// en las peticiones.
//
// Todo sale de VITE_API_URL.
//
// =========================================================

const API_URL = (
  import.meta.env.VITE_API_URL ||
  "http://localhost:8000/api"
).replace(/\/+$/, "");


// =========================================================
// API URL
// =========================================================

function getApiUrl(path = "") {
  const cleanPath = String(path).startsWith("/")
    ? path
    : `/${path}`;

  return `${API_URL}${cleanPath}`;
}


// =========================================================
// BACKEND ORIGIN
// =========================================================
//
// Convierte:
//
// http://localhost:8000/api
//
// en:
//
// http://localhost:8000
//
// o:
//
// https://api.debutscrop.com/api
//
// en:
//
// https://api.debutscrop.com
//
// =========================================================

function getBackendOrigin() {
  return API_URL
    .replace(/\/api\/?$/, "")
    .replace(/\/+$/, "");
}


// =========================================================
// IMAGE URL
// =========================================================

function getImageUrl(image) {

  if (!image) {
    return blogBg;
  }

  if (
    typeof image !== "string"
  ) {
    return blogBg;
  }

  const value =
    image.trim();

  if (!value) {
    return blogBg;
  }


  // Imagen absoluta

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }


  // Imagen relativa de Django

  if (
    value.startsWith("/")
  ) {
    return `${getBackendOrigin()}${value}`;
  }


  // Imagen relativa sin /

  return `${getBackendOrigin()}/${value.replace(/^\/+/, "")}`;
}


// =========================================================
// DATE
// =========================================================

function formatDate(date) {

  if (!date) {
    return "";
  }

  const parsedDate =
    new Date(date);

  if (
    Number.isNaN(
      parsedDate.getTime()
    )
  ) {
    return String(date);
  }

  return parsedDate.toLocaleDateString(
    "es-CO",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  );
}


// =========================================================
// CATEGORY NAME
// =========================================================

function getCategoryName(article) {

  if (!article) {
    return "BLOG";
  }

  if (
    article.category_name
  ) {
    return article.category_name;
  }

  if (
    article.category &&
    typeof article.category === "object"
  ) {
    return (
      article.category.name ||
      "BLOG"
    );
  }

  if (
    typeof article.category === "string"
  ) {
    return (
      article.category ||
      "BLOG"
    );
  }

  return "BLOG";
}


// =========================================================
// CATEGORY SLUG
// =========================================================

function getCategorySlug(article) {

  if (!article) {
    return "";
  }

  if (
    article.category_slug
  ) {
    return article.category_slug;
  }

  if (
    article.category &&
    typeof article.category === "object"
  ) {
    return (
      article.category.slug ||
      ""
    );
  }

  return "";
}


// =========================================================
// AUTHOR
// =========================================================

function getAuthorName(article) {

  if (!article) {
    return "";
  }

  if (
    article.author_name
  ) {
    return article.author_name;
  }

  if (
    article.author &&
    typeof article.author === "object"
  ) {
    return (
      article.author.name ||
      article.author.username ||
      ""
    );
  }

  if (
    typeof article.author === "string"
  ) {
    return article.author;
  }

  return "";
}


// =========================================================
// READING TIME
// =========================================================

function getReadingTime(article) {

  if (!article) {
    return "";
  }

  if (
    article.reading_time
  ) {
    return `${article.reading_time} min`;
  }

  if (
    article.read_time
  ) {
    return article.read_time;
  }

  if (
    article.readTime
  ) {
    return article.readTime;
  }

  return "";
}


// =========================================================
// NORMALIZE ARTICLE
// =========================================================

function normalizeArticle(article) {

  if (!article) {
    return null;
  }

  return {
    ...article,

    image: getImageUrl(
      article.cover_image ||
      article.image ||
      article.cover ||
      ""
    ),

    categoryName:
      getCategoryName(article),

    categorySlug:
      getCategorySlug(article),

    date:
      formatDate(
        article.published_at ||
        article.created_at
      ),

    authorName:
      getAuthorName(article),

    readTime:
      getReadingTime(article),

    excerpt:
      article.excerpt ||
      "",
  };
}


// =========================================================
// GET API RESULTS
// =========================================================
//
// Soporta:
//
// [
//   {...},
//   {...}
// ]
//
// y:
//
// {
//   count: 10,
//   results: [...]
// }
//
// =========================================================

function getResults(data) {

  if (
    Array.isArray(data)
  ) {
    return data;
  }

  if (
    Array.isArray(
      data?.results
    )
  ) {
    return data.results;
  }

  return [];
}


// =========================================================
// SIDEBAR
// =========================================================

function Sidebar({
  categories,
  popular,
  search,
  setSearch,
  pageSettings,
}) {

  // =======================================================
  // SEARCH
  // =======================================================

  function handleSearch(event) {

    event.preventDefault();

    const value =
      search.trim();

    if (!value) {

      window.location.href =
        "/blog";

      return;
    }

    window.location.href =
      `/blog?search=${encodeURIComponent(
        value
      )}`;
  }


  // =======================================================
  // SETTINGS
  // =======================================================

  const subscribeTitle =
    pageSettings?.subscribe_title ||
    "SUSCRÍBETE";

  const subscribeText =
    pageSettings?.subscribe_text ||
    "Recibe nuestras novedades, promociones y artículos exclusivos directamente en tu correo.";

  const subscribePlaceholder =
    pageSettings?.subscribe_placeholder ||
    "Tu correo electrónico";

  const subscribeButton =
    pageSettings?.subscribe_button_text ||
    "SUBSCRIBIRME";

  const categoriesTitle =
    pageSettings?.categories_title ||
    "CATEGORÍAS";

  const popularTitle =
    pageSettings?.popular_title ||
    "ARTÍCULOS POPULARES";


  return (
    <aside className="blog-sidebar">

      {/* =================================================
          SEARCH
      ================================================= */}

      <form
        className="sidebar-search"
        onSubmit={handleSearch}
      >

        <span>
          ⌕
        </span>

        <input
          type="search"
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          placeholder={
            pageSettings?.search_placeholder ||
            "Buscar en el blog..."
          }
          aria-label="Buscar en el blog"
        />

      </form>


      {/* =================================================
          CATEGORIES
      ================================================= */}

      <section className="sidebar-box">

        <h3>
          ♛ &nbsp;
          {categoriesTitle}
        </h3>

        {categories.length > 0 ? (

          categories.map(
            (category) => {

              const categorySlug =
                category.slug ||
                category.id;

              return (
                <Link
                  key={
                    category.id ||
                    category.slug
                  }
                  to={`/blog?category=${encodeURIComponent(
                    categorySlug
                  )}`}
                >

                  <span>
                    {category.name}
                  </span>

                  <b>
                    {Number(
                      category.article_count ||
                      0
                    )}
                  </b>

                </Link>
              );
            }
          )

        ) : (

          <span>
            No hay categorías disponibles.
          </span>

        )}

      </section>


      {/* =================================================
          POPULAR
      ================================================= */}

      <section className="sidebar-box">

        <h3>
          ♨ &nbsp;
          {popularTitle}
        </h3>

        {popular.length > 0 ? (

          popular.map(
            (post) => (

              <Link
                className="popular-post"
                to={`/blog/${post.slug}`}
                key={
                  post.slug ||
                  post.id
                }
              >

                <img
                  src={
                    post.image ||
                    blogBg
                  }
                  alt={
                    post.title ||
                    ""
                  }
                  loading="lazy"
                  onError={(event) => {

                    if (
                      event.currentTarget.src !==
                      blogBg
                    ) {
                      event.currentTarget.src =
                        blogBg;
                    }

                  }}
                />

                <span>

                  <strong>
                    {post.title}
                  </strong>

                  <small>
                    {post.date}
                  </small>

                </span>

              </Link>

            )
          )

        ) : (

          <span>
            No hay artículos populares.
          </span>

        )}

      </section>


      {/* =================================================
          SUBSCRIBE
      ================================================= */}

      <section className="subscribe-box">

        <h3>
          ✉ &nbsp;
          {subscribeTitle}
        </h3>

        <p>
          {subscribeText}
        </p>

        <input
          type="email"
          placeholder={
            subscribePlaceholder
          }
          aria-label={
            subscribePlaceholder
          }
        />

        <button type="button">
          {subscribeButton}
        </button>

      </section>


      {/* =================================================
          PROMO
      ================================================= */}

      <div
        className="sidebar-promo"
        style={{
          backgroundImage:
            `url(${
              getImageUrl(
                pageSettings?.promo_image
              )
            })`,
        }}
      >

        <strong>
          ♛
        </strong>

        <span>

          {pageSettings?.promo_text ? (
            pageSettings.promo_text
          ) : (
            <>
              EL CONOCIMIENTO
              <br />
              TAMBIÉN ES CULTIVO
            </>
          )}

        </span>

      </div>

    </aside>
  );
}


// =========================================================
// BLOG
// =========================================================

export default function Blog() {

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();


  // =======================================================
  // STATE
  // =======================================================

  const [
    articles,
    setArticles,
  ] = useState([]);

  const [
    categories,
    setCategories,
  ] = useState([]);

  const [
    popular,
    setPopular,
  ] = useState([]);

  const [
    pageSettings,
    setPageSettings,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState(
    searchParams.get(
      "search"
    ) || ""
  );

  const [
    currentPage,
    setCurrentPage,
  ] = useState(
    Math.max(
      1,
      Number(
        searchParams.get(
          "page"
        ) || 1
      )
    )
  );


  // =======================================================
  // URL FILTERS
  // =======================================================

  const categoryFilter =
    searchParams.get(
      "category"
    ) || "";

  const searchFilter =
    searchParams.get(
      "search"
    ) || "";


  // =======================================================
  // POSTS PER PAGE
  // =======================================================

  const postsPerPage =
    useMemo(() => {

      const configured =
        Number(
          pageSettings?.posts_per_page
        );

      if (
        Number.isFinite(
          configured
        ) &&
        configured > 0
      ) {
        return configured;
      }

      return 6;

    }, [
      pageSettings,
    ]);


  // =======================================================
  // TOTAL PAGES
  // =======================================================

  const [
    totalPages,
    setTotalPages,
  ] = useState(1);


  // =======================================================
  // LOAD SETTINGS
  // =======================================================

  useEffect(() => {

    let cancelled = false;

    async function loadSettings() {

      try {

        const response =
          await fetch(
            getApiUrl(
              "/blog/settings/"
            ),
            {
              headers: {
                Accept:
                  "application/json",
              },
            }
          );

        if (!response.ok) {
          return;
        }

        const data =
          await response.json();

        if (!cancelled) {
          setPageSettings(
            data
          );
        }

      } catch (err) {

        console.error(
          "BLOG SETTINGS:",
          err
        );

      }

    }

    loadSettings();

    return () => {
      cancelled = true;
    };

  }, []);


  // =======================================================
  // LOAD CATEGORIES
  // =======================================================

  useEffect(() => {

    let cancelled = false;

    async function loadCategories() {

      try {

        const response =
          await fetch(
            getApiUrl(
              "/blog/categories/?is_active=true"
            ),
            {
              headers: {
                Accept:
                  "application/json",
              },
            }
          );

        if (!response.ok) {

          throw new Error(
            `HTTP ${response.status}`
          );

        }

        const data =
          await response.json();

        if (!cancelled) {

          setCategories(
            getResults(data)
          );

        }

      } catch (err) {

        console.error(
          "BLOG CATEGORIES:",
          err
        );

        if (!cancelled) {
          setCategories([]);
        }

      }

    }

    loadCategories();

    return () => {
      cancelled = true;
    };

  }, []);


  // =======================================================
  // LOAD POPULAR
  // =======================================================

  useEffect(() => {

    let cancelled = false;

    async function loadPopular() {

      try {

        const response =
          await fetch(
            getApiUrl(
              "/blog/articles/popular/?limit=5"
            ),
            {
              headers: {
                Accept:
                  "application/json",
              },
            }
          );

        if (!response.ok) {

          throw new Error(
            `HTTP ${response.status}`
          );

        }

        const data =
          await response.json();

        if (!cancelled) {

          setPopular(
            getResults(data)
              .map(
                normalizeArticle
              )
              .filter(Boolean)
          );

        }

      } catch (err) {

        console.error(
          "BLOG POPULAR:",
          err
        );

        if (!cancelled) {
          setPopular([]);
        }

      }

    }

    loadPopular();

    return () => {
      cancelled = true;
    };

  }, []);


  // =======================================================
  // LOAD ARTICLES
  // =======================================================

  useEffect(() => {

    const controller =
      new AbortController();

    async function loadArticles() {

      setLoading(true);
      setError("");

      try {

        const params =
          new URLSearchParams();

        params.set(
          "page",
          String(
            currentPage
          )
        );

        params.set(
          "page_size",
          String(
            postsPerPage
          )
        );


        // CATEGORY

        if (
          categoryFilter
        ) {

          params.set(
            "category_slug",
            categoryFilter
          );

        }


        // SEARCH

        if (
          searchFilter
        ) {

          params.set(
            "search",
            searchFilter
          );

        }


        const response =
          await fetch(
            getApiUrl(
              `/blog/articles/?${params.toString()}`
            ),
            {
              method: "GET",
              headers: {
                Accept:
                  "application/json",
              },
              signal:
                controller.signal,
            }
          );


        if (!response.ok) {

          throw new Error(
            `HTTP ${response.status}`
          );

        }


        const data =
          await response.json();


        const results =
          getResults(data);


        const count =
          Number(
            data?.count ??
            results.length
          );


        const calculatedPages =
          count > 0
            ? Math.ceil(
                count /
                postsPerPage
              )
            : 1;


        setArticles(
          results
            .map(
              normalizeArticle
            )
            .filter(Boolean)
        );


        setTotalPages(
          Math.max(
            1,
            calculatedPages
          )
        );

      } catch (err) {

        if (
          err.name ===
          "AbortError"
        ) {
          return;
        }

        console.error(
          "BLOG ARTICLES:",
          err
        );

        setArticles([]);

        setTotalPages(1);

        setError(
          "No fue posible conectar con el contenido del blog."
        );

      } finally {

        if (
          !controller.signal.aborted
        ) {
          setLoading(false);
        }

      }

    }

    loadArticles();

    return () => {
      controller.abort();
    };

  }, [
    currentPage,
    categoryFilter,
    searchFilter,
    postsPerPage,
  ]);


  // =======================================================
  // SYNC SEARCH
  // =======================================================

  useEffect(() => {

    setSearch(
      searchFilter
    );

  }, [
    searchFilter,
  ]);


  // =======================================================
  // SYNC PAGE
  // =======================================================

  useEffect(() => {

    const urlPage =
      Math.max(
        1,
        Number(
          searchParams.get(
            "page"
          ) || 1
        )
      );

    setCurrentPage(
      urlPage
    );

  }, [
    searchParams,
  ]);


  // =======================================================
  // CURRENT CATEGORY
  // =======================================================

  const currentCategory =
    categories.find(
      (category) =>
        String(
          category.slug
        ) ===
        String(
          categoryFilter
        )
    );


  // =======================================================
  // TITLE
  // =======================================================

  const pageTitle =
    currentCategory?.name
      ? currentCategory.name
      : searchFilter
        ? `RESULTADOS: ${searchFilter}`
        : (
            pageSettings?.latest_title ||
            "ÚLTIMOS ARTÍCULOS"
          );


  // =======================================================
  // HERO
  // =======================================================

  const heroEyebrow =
    pageSettings?.hero_eyebrow ||
    "TIPS, NOTICIAS, CULTURA Y MUCHO MÁS";

  const heroTitle =
    pageSettings?.hero_title ||
    "NUESTRO";

  const heroHighlight =
    pageSettings?.hero_highlight ||
    "BLOG";

  const heroText =
    pageSettings?.hero_text ||
    "En nuestro blog compartimos las últimas novedades del mundo de los vaporizadores, destilados, cultura y consejos para que disfrutes al máximo tu experiencia.";

  const heroImage =
    getImageUrl(
      pageSettings?.hero_image
    );


  // =======================================================
  // PAGE NAVIGATION
  // =======================================================

  function goToPage(page) {

    const safePage =
      Math.max(
        1,
        Math.min(
          page,
          totalPages
        )
      );


    const params =
      new URLSearchParams(
        searchParams
      );

    params.set(
      "page",
      String(
        safePage
      )
    );


    setSearchParams(
      params
    );


    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  }


  // =======================================================
  // CLEAR FILTERS
  // =======================================================

  function clearFilters() {

    setSearch("");

    setSearchParams({});

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  }


  // =======================================================
  // BLOG HOME
  // =======================================================

  function handleBlogHome() {

    setSearch("");

    setSearchParams({});

  }


  // =======================================================
  // PAGE NUMBERS
  // =======================================================

  const pageNumbers =
    useMemo(() => {

      const maxButtons = 5;

      if (
        totalPages <=
        maxButtons
      ) {

        return Array.from(
          {
            length:
              totalPages,
          },
          (_, index) =>
            index + 1
        );

      }


      let start =
        Math.max(
          1,
          currentPage - 2
        );

      let end =
        Math.min(
          totalPages,
          start +
            maxButtons -
            1
        );


      if (
        end -
          start +
          1 <
        maxButtons
      ) {

        start =
          Math.max(
            1,
            end -
              maxButtons +
              1
          );

      }


      return Array.from(
        {
          length:
            end -
            start +
            1,
        },
        (_, index) =>
          start + index
      );

    }, [
      currentPage,
      totalPages,
    ]);


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div
      className="blog-page"
      style={{
        "--blog-bg":
          `url(${blogBg})`,
      }}
    >

      {/* =================================================
          NAVBAR
      ================================================= */}

      <Navbar />


      {/* =================================================
          MAIN
      ================================================= */}

      <main>


        {/* =================================================
            HERO
        ================================================= */}

        <section
          className="blog-hero"
          style={{
            backgroundImage:
              `url(${heroImage})`,
          }}
        >

          <div className="blog-hero-overlay">

            <p className="eyebrow">
              {heroEyebrow}
            </p>

            <h1>

              {heroTitle}

              <em>
                {heroHighlight}
              </em>

            </h1>

            <p className="hero-copy">
              {heroText}
            </p>

          </div>

        </section>


        {/* =================================================
            BLOG LAYOUT
        ================================================= */}

        <div className="blog-layout">


          {/* =================================================
              CONTENT
          ================================================= */}

          <section className="blog-content">


            {/* =================================================
                HEADING
            ================================================= */}

            <div className="section-heading">

              <h2>
                ♛ {pageTitle}
              </h2>

              <Link
                to="/blog"
                onClick={
                  handleBlogHome
                }
              >
                Ver todos los artículos →
              </Link>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {error && (

              <div className="blog-error">

                <span className="state-label">
                  DEBUTS CROP · BLOG / ERROR
                </span>

                <h3>
                  NO PUDIMOS CARGAR
                  <br />
                  <em>
                    EL BLOG.
                  </em>
                </h3>

                <p>
                  {error}
                </p>

                <button
                  type="button"
                  onClick={() =>
                    window.location.reload()
                  }
                >
                  REINTENTAR
                </button>

              </div>

            )}


            {/* =================================================
                LOADING
            ================================================= */}

            {loading &&
              !error && (

                <div className="post-grid">

                  {Array.from({
                    length:
                      postsPerPage,
                  }).map(
                    (_, index) => (

                      <article
                        className="post-card"
                        key={
                          `loading-${index}`
                        }
                      >

                        <div className="post-image">

                          <img
                            src={blogBg}
                            alt=""
                          />

                        </div>

                        <div className="post-body">

                          <small>
                            CARGANDO...
                          </small>

                          <h3>
                            Cargando artículo...
                          </h3>

                          <p>
                            Estamos cargando
                            el contenido del blog.
                          </p>

                        </div>

                      </article>

                    )
                  )}

                </div>

              )}


            {/* =================================================
                ARTICLES
            ================================================= */}

            {!loading &&
              !error &&
              articles.length > 0 && (

                <div className="post-grid">

                  {articles.map(
                    (post) => (

                      <article
                        className="post-card"
                        key={
                          post.slug ||
                          post.id
                        }
                      >

                        <Link
                          to={`/blog/${post.slug}`}
                          className="post-image"
                        >

                          <img
                            src={
                              post.image
                            }
                            alt={
                              post.title ||
                              "Artículo Debuts Crop"
                            }
                            loading="lazy"
                            onError={(event) => {

                              if (
                                event.currentTarget.src !==
                                blogBg
                              ) {

                                event.currentTarget.src =
                                  blogBg;

                              }

                            }}
                          />

                          <span>
                            {
                              post.categoryName
                            }
                          </span>

                        </Link>


                        <div className="post-body">

                          <small>
                            {
                              post.date
                            }
                          </small>

                          <h3>
                            {
                              post.title
                            }
                          </h3>

                          <p>
                            {
                              post.excerpt
                            }
                          </p>

                          <Link
                            to={`/blog/${post.slug}`}
                            className="read-more"
                          >
                            Leer más →
                          </Link>

                        </div>

                      </article>

                    )
                  )}

                </div>

              )}


            {/* =================================================
                EMPTY STATE
            ================================================= */}

            {!loading &&
              !error &&
              articles.length === 0 && (

                <div className="blog-empty">

                  <span className="state-label">
                    DEBUTS CROP · BLOG / 00
                  </span>


                  {/* =========================================
                      SEARCH / FILTER EMPTY
                  ========================================= */}

                  {categoryFilter ||
                  searchFilter ? (

                    <>

                      <h3>

                        NO ENCONTRAMOS

                        <br />

                        <em>
                          ARTÍCULOS.
                        </em>

                      </h3>

                      <p>
                        No encontramos contenido
                        que coincida con los filtros
                        seleccionados.
                        <br />
                        Prueba con otra búsqueda o
                        explora nuevamente todo
                        nuestro contenido.
                      </p>

                      <button
                        type="button"
                        onClick={
                          clearFilters
                        }
                      >
                        VER TODO EL BLOG
                      </button>

                    </>

                  ) : (

                    /* =======================================
                       NO BLOG CONTENT
                    ======================================= */

                    <>

                      <h3>

                        EL BLOG ESTÁ

                        <br />

                        <em>
                          CRECIENDO.
                        </em>

                      </h3>

                      <p>
                        Estamos preparando nuevos
                        artículos, consejos, novedades
                        y contenido exclusivo para
                        nuestra comunidad.
                        <br />
                        Muy pronto encontrarás aquí
                        nuevas historias de
                        Debuts Crop.
                      </p>

                      <Link
                        to="/catalog"
                        className="blog-empty-button"
                      >
                        EXPLORAR CATÁLOGO →

                      </Link>

                    </>

                  )}

                </div>

              )}


            {/* =================================================
                PAGINATION
            ================================================= */}

            {!loading &&
              !error &&
              articles.length > 0 &&
              totalPages > 1 && (

                <nav
                  className="pagination"
                  aria-label="Paginación del blog"
                >

                  {/* PREVIOUS */}

                  <button
                    type="button"
                    disabled={
                      currentPage <= 1
                    }
                    onClick={() =>
                      goToPage(
                        currentPage - 1
                      )
                    }
                    aria-label="Página anterior"
                  >
                    ←
                  </button>


                  {/* FIRST PAGE */}

                  {pageNumbers[0] > 1 && (

                    <>
                      <button
                        type="button"
                        onClick={() =>
                          goToPage(1)
                        }
                      >
                        1
                      </button>

                      {pageNumbers[0] >
                        2 && (
                        <span>
                          …
                        </span>
                      )}

                    </>

                  )}


                  {/* PAGE NUMBERS */}

                  {pageNumbers.map(
                    (page) => (

                      <button
                        type="button"
                        key={page}
                        className={
                          currentPage ===
                          page
                            ? "active"
                            : ""
                        }
                        onClick={() =>
                          goToPage(
                            page
                          )
                        }
                        aria-current={
                          currentPage ===
                          page
                            ? "page"
                            : undefined
                        }
                      >
                        {page}
                      </button>

                    )
                  )}


                  {/* LAST PAGE */}

                  {pageNumbers[
                    pageNumbers.length -
                      1
                  ] < totalPages && (

                    <>

                      {pageNumbers[
                        pageNumbers.length -
                          1
                      ] <
                        totalPages -
                          1 && (
                        <span>
                          …
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={() =>
                          goToPage(
                            totalPages
                          )
                        }
                      >
                        {totalPages}
                      </button>

                    </>

                  )}


                  {/* NEXT */}

                  <button
                    type="button"
                    disabled={
                      currentPage >=
                      totalPages
                    }
                    onClick={() =>
                      goToPage(
                        currentPage + 1
                      )
                    }
                    aria-label="Página siguiente"
                  >
                    →
                  </button>

                </nav>

              )}

          </section>


          {/* =================================================
              SIDEBAR
          ================================================= */}

          <Sidebar
            categories={
              categories
            }
            popular={
              popular
            }
            search={
              search
            }
            setSearch={
              setSearch
            }
            pageSettings={
              pageSettings
            }
          />

        </div>

      </main>


      {/* =================================================
          FOOTER
      ================================================= */}

      <Footer />

    </div>
  );
}